import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import AnimatedBackground from "./AnimatedBackground";
import BrowserMockup from "./BrowserMockup";
import TypingEffect from "./TypingEffect";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMagnetic } from "@/hooks/useMagnetic";
import { ease, duration } from "@/lib/motion";

const HeroSection = () => {
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  useMagnetic(ctaRef);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.5], [0, -80]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[100dvh] items-center justify-center bg-charcoal lg:sticky top-0 overflow-hidden"
      style={{ zIndex: 1, paddingTop: isMobile ? "calc(var(--navbar-height, 4.75rem) - 1.5rem)" : "var(--navbar-height, 4.75rem)", paddingBottom: "clamp(120px, 16vh, 180px)" }}
      data-dark-section
    >
      {/* Animated background */}
      <div className="absolute inset-0">
        <AnimatedBackground />
      </div>

      {/* Radial gradient glows */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: [
            "radial-gradient(ellipse 60% 50% at 15% 50%, hsl(160 30% 12% / 0.5), transparent)",
            "radial-gradient(ellipse 50% 50% at 85% 40%, hsl(140 20% 14% / 0.4), transparent)",
            "radial-gradient(ellipse 80% 40% at 50% 100%, hsl(30 8% 10% / 0.3), transparent)",
          ].join(", "),
        }}
      />

      {/* Bottom gradient blend into next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-40 md:h-56 pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to bottom, transparent, hsl(var(--section-warm)))" }}
      />

      <motion.div
        className="container relative z-10 mx-auto px-6 md:px-8"
        style={prefersReducedMotion ? {} : { opacity: contentOpacity, y: contentY }}
      >
        {/* Desktop: 2-column layout */}
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-12 lg:items-center">
          {/* Left: copy — unified single-beat fade */}
          <motion.div
            className="max-w-2xl lg:max-w-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: duration.deliberate, ease: ease.smooth }}
          >
            {/* Location badge */}
            <div className="mb-5 lg:mb-6">
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-cream/10 bg-cream/5 px-3.5 py-1.5 text-xs font-medium text-cream/60">
                <MapPin className="h-3 w-3 text-sage" />
                Located in Raleigh, NC
              </span>
            </div>

            {/* Eyebrow */}
            <p className="mb-3 lg:mb-5 text-[11px] font-semibold uppercase tracking-[0.35em] text-sage sm:text-xs md:text-sm">
              Web Design for Local Contractors
            </p>

            {/* Headline */}
            <h1
              aria-label="Better websites. More jobs."
              data-text="Better websites. More jobs."
              className="h1-stack mb-4 lg:mb-6 font-heading text-[2.25rem] leading-[1.05] text-cream sm:text-5xl md:text-[3.5rem] lg:text-[4rem] xl:text-[4.5rem] font-bold"
            >
              <span aria-hidden="true">
                {prefersReducedMotion ? (
                  <>
                    Better websites. More{" "}
                    <span className="text-accent-sweep">jobs</span>.
                  </>
                ) : (
                  <TypingEffect
                    segments={[
                      { text: "Better websites. More " },
                      { text: "jobs", className: "text-accent-sweep" },
                      { text: "." },
                    ]}
                    typingSpeed={40}
                  />
                )}
              </span>
            </h1>

            {/* Body */}
            <p className="mb-7 lg:mb-9 max-w-xl text-sm leading-relaxed text-cream/80 md:text-base lg:text-lg">
              Custom-built sites for <span className="text-cream font-medium">Raleigh-Durham</span> contractors. Ranked higher on Google. Designed to turn clicks into calls.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <a
                ref={ctaRef}
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#contact");
                  if (el) {
                    const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--navbar-height")) * 16 || 76;
                    window.scrollTo({ top: window.pageYOffset + el.getBoundingClientRect().top - navH, behavior: "smooth" });
                  }
                }}
                className="relative inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3.5 text-[13px] font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-[background-color,box-shadow] duration-300 hover:bg-forest-light hover:shadow-xl hover:shadow-primary/25 sm:px-7 sm:py-3.5 md:text-sm active:scale-[0.98] overflow-hidden will-change-transform"
              >
                <span className="relative z-10">Book a Free Consultation</span>
              </a>
              <a
                href="#showcase"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#showcase");
                  if (el) {
                    const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--navbar-height")) * 16 || 76;
                    window.scrollTo({ top: window.pageYOffset + el.getBoundingClientRect().top - navH, behavior: "smooth" });
                  }
                }}
                className="inline-flex items-center justify-center rounded-lg border border-cream/50 px-6 py-3.5 text-[13px] font-semibold text-cream/80 transition-[border-color,color,transform] duration-300 hover:border-cream/60 hover:text-cream hover:-translate-y-0.5 sm:px-7 sm:py-3.5 md:text-sm active:scale-[0.98]"
              >
                See Our Work
              </a>
            </div>
          </motion.div>

          {/* Right: browser mockup — paints in parallel with the typed headline
             (ungated to minimize LCP; the mockup image is the LCP candidate). */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: duration.deliberate, ease: ease.smooth }}
            className="mt-10 flex items-center justify-center lg:mt-0 lg:pl-4"
          >
            <BrowserMockup ready />
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: duration.smooth, delay: 0.4, ease: ease.smooth }}
        className="absolute bottom-20 left-1/2 -translate-x-1/2 hidden lg:flex z-[3]"
      >
        <a
          href="#about"
          onClick={(e) => {
            e.preventDefault();
            const el = document.querySelector("#about");
            if (el) {
              const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--navbar-height")) * 16 || 76;
              window.scrollTo({ top: window.pageYOffset + el.getBoundingClientRect().top - navH, behavior: "smooth" });
            }
          }}
          className="inline-flex flex-col items-center gap-2 text-cream/40 transition-colors duration-300 hover:text-cream/60"
        >
          <span className="text-[10px] uppercase tracking-[0.2em] font-medium">Scroll</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </a>
      </motion.div>
    </section>
  );
};

export default HeroSection;
